import React, { useRef, useContext, useEffect, useState, useCallback } from "react"
import { Card, Button, Form, InputGroup, FormControl, OverlayTrigger, Tooltip } from 'react-bootstrap'
import DatePicker from "react-datepicker"
import 'react-datepicker/dist/react-datepicker.css'
import axios from "axios";

import { getCurrencySymbol, getCountry } from '@helper/getConfigValues';
import BulkReviewsContext, { type BulkReviewsContextType } from '../context'
import { NotificationContext, DEFAULT_DELAY } from "@components/Notification/Notification"
import type { LocationSearchResult, SuburbSearchResultProps, YourDetailsProps } from "../types";
import ClientOnly from '../../../ClientOnly';
import { useDebounce } from "@hooks/useDebounce";
import { locationSearch } from "@api/locationSearch";
import { LOCAL_STORAGE_REVIEWS_KEY } from "../constants";

import scss from '../BulkReviews.module.scss'
import { type NumberFormatValues, type SourceInfo, NumericFormat } from "react-number-format";
import type { UserAvatarProps } from "@components/UserAvatar/UserAvatar";

import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import Checkbox from "@ui/Checkbox";
import Loading from "@ui/Loading";
import { ga4Generic } from "@helper/gtm";

import "./Sidebar.scss";

const UserAvatarComponent = React.lazy(() => import('@components/UserAvatar/UserAvatar'));

type LocationSearchResultContainerProps = {
    searchTerm: string;
    locationSearchUrl: string;
    onSelectLocation: (selectedLocation: string) => void;
}

const LocationSearchResultContainer = ({ searchTerm, locationSearchUrl, onSelectLocation } : LocationSearchResultContainerProps) => {
    const [locationResults, setLocationResults] = useState<LocationSearchResult[]>([])

    useEffect(() => {
        const getSearchResults = async () => {
            if(searchTerm.length > 2) {
                locationSearch(locationSearchUrl, searchTerm, 5)
                    .then(result => {
                        if (!!result && Array.isArray(result) && result.length > 0) {
                            setLocationResults(result);
                    }
                        else {
                            setLocationResults([]);
                }
                    })
                    .catch(e => {
                        setLocationResults([]);
                    });
            } else {
                setLocationResults([]);
            }
        }
        getSearchResults()
    }, [searchTerm])

    const locationSelected = useCallback((selectedLocation: string) => {
        onSelectLocation(selectedLocation);
        setLocationResults([]);
    }, [setLocationResults])

    return (
        <>
            {locationResults && locationResults.length > 0 && (
                <div>
                    <ul className={scss.locationSearchResultList}>
                        {locationResults.map((result: LocationSearchResult, index) => {
                            return <Location key={index} suburb={result.suburb} postcode={result.postcode} onSelect={locationSelected} />
                        })}
                    </ul>
                </div>
            )}
        </>
    )
}

const Location = ({suburb, postcode, onSelect}: SuburbSearchResultProps) => {
    return (
        <div className={scss.locationSearchResult} data-testid='location-search-result' onClick={() => onSelect(`${suburb}, ${postcode}`)}>{`${suburb}, ${postcode}`}</div>
    )
}

const YourDetails = ({ locationSearchUrl, loginUrl, registerUrl, facebookAppId } : YourDetailsProps) => {
    const UK_LOCATION_PATTERN = /^[a-zA-Z\s]+, [a-zA-Z0-9\s]+$/;
    const AU_LOCATION_PATTERN = /^[a-zA-Z\s]+, \d+$/;
    const countryCode = getCountry();
    const isUK = (countryCode === 'uk');
    const firstnameRef = useRef<HTMLInputElement>(null);
    const lastnameRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);
    const locationRef = useRef<HTMLInputElement>(null);
    const weddingCostRef = useRef<HTMLInputElement>(null);
   
    const [weddingDate, setWeddingDate] = useState<Date | null>(null);
    
    const { categoryInfo, loggedInUser, isLoggedIn, reviews, getLoggedInUser, updateUser, setShowValidationMessages } = useContext<BulkReviewsContextType>(BulkReviewsContext);
    const [validated, setValidated] = useState<boolean>(false);
    const [isComp, setIsComp] = useState<boolean>(true);
    const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
    const [locationSearchTerm, setLocationSearchTerm] = useState<string>('');
    const [totalCost, setTotalCost] = useState<number | undefined>();
    const { setNotice, setDelay } = useContext(NotificationContext);

    const deferredSearchTerm = useDebounce(locationSearchTerm, 400);
    const currencySymbol = getCurrencySymbol();

    const showLoginModal = useCallback(() => {        
        window.location.href = "/login?returnUrl=" + window.location.pathname;        
    }, [])    

    const handleIsComp = useCallback(() => setIsComp(prev => !prev), [setIsComp]); 

    useEffect(() => {
        if(!!loggedInUser) {
            if(firstnameRef.current) {
                firstnameRef.current.value = loggedInUser?.FirstName ?? ''
            }
            if(lastnameRef.current) {
                lastnameRef.current.value = loggedInUser?.LastName ?? ''
            }
            if(emailRef.current) {
                emailRef.current.value = loggedInUser?.Email ?? ''
            }
            if(locationRef.current) {
                const city = loggedInUser?.City ?? ''
                const postcode = loggedInUser?.Postcode ?? ''
                locationRef.current.value = (!!city || !!postcode) ? `${city}, ${postcode}` : ''
            }
            if(!!loggedInUser.WeddingDate) { 
                const formattedDate = new Date(loggedInUser?.WeddingDate)
                setWeddingDate(formattedDate)
            }
        }
    }, [loggedInUser]);

    const displayError = useCallback((message: string) => {
        setDelay(DEFAULT_DELAY);
        setNotice(<div>{message}</div>);
    }, []);

    const submitReview = async (event: React.FormEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        !!setShowValidationMessages && setShowValidationMessages(true);
        event.preventDefault();
        event.stopPropagation();

        const completed = reviews.filter(r => r.complete);
        const locationValue = locationRef.current?.value;
        
        const requiredFieldsDiv = document.querySelector("div.required-fields");

        if (requiredFieldsDiv) {
            requiredFieldsDiv.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        if(!validWeddingDate(weddingDate))
        {
            displayError('Wedding Date must be in the past');
        }
        else if(!!locationValue && !validLocation(locationValue))
        {
            displayError('Please select a location from the dropdown');
        }
        else if (completed.length !== reviews.length) {
            displayError('Please complete all in progress reviews before submitting');
        }
        else if (completed.length === 0) {
            displayError('Please complete at least one review');
        }
        else if(!!form.checkValidity() && (!!loggedInUser?.ImageId || !!loggedInUser?.ProfileImage)) {
            setFormSubmitting(true);
            const data = JSON.stringify({
                reviews: completed,
                user: {
                    UserId: loggedInUser?.UserId,
                    FirstName: firstnameRef.current?.value,
                    LastName: lastnameRef.current?.value,
                    Email: emailRef.current?.value,
                    WeddingDate: (!!weddingDate ? new Date(weddingDate).toISOString().split('T')[0] : null),
                    Location: locationRef.current?.value,
                    WeddingCost: totalCost,
                    ProfileImage: loggedInUser?.ProfileImage,
                    ImageId: loggedInUser?.ImageId,
                },
                userCompetitionParticipate: isComp,
            });

            completed.forEach(r => ga4Generic({
                'event': 'reviews',
                'supplierName': r.supplierName,
                'ewPageType': 'reviews-page',
                'ewInteractionAction': 'single-review-submission',
                'supplierCategory': categoryInfo?.categories?.find(c => c.categoryId === r.supplierCategoryId)?.name,
                'reviewRating': r.userStarRating,
            }));

            try {
                axios.post(
                    '/api/EasyDirectoryAPI/submit-user-reviews',
                    data,
                    { headers: { 'Content-Type': 'application/json' } }
                ).then((result: { status: number; data: { message: string } }) => {
                    if (result.status !== 200) {
                        displayError(result.data.message);
                    }
                    else if (!!window) {
                        localStorage.removeItem(LOCAL_STORAGE_REVIEWS_KEY);
                        window.location.href = '/reviews/thankyou/';
                    }    
                    setFormSubmitting(false);
                }).catch((err: { response: { data: { Message: string } } }) => {
                    displayError(err.response?.data?.Message);
                    setFormSubmitting(false);
                });
            }
            catch (e) {
                displayError('Review submission failed');
                setFormSubmitting(false);
            }
        }
        setValidated(true);
    }

    const validLocation = (location: string) => {
        if(location.length > 0) {
            const locationPattern = isUK ? UK_LOCATION_PATTERN : AU_LOCATION_PATTERN;
            const locationValid = locationPattern.test(location);
            return locationValid;
        }
        return false;
    }

    const validWeddingDate = (weddingDate: Date | null) => {
        if(!weddingDate) {
           return false; 
        }
        const today = new Date();
        if(!!weddingDate && weddingDate > today) {
            return false;
        }
        return true;
    }

    const onLocationChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const location = event.target.value;
        setLocationSearchTerm(location)
    }

    const onSelectLocation = (selectedLocation: string) => {
        if(locationRef.current) {
            locationRef.current.value = selectedLocation
        }
    }

    const handleCostChange = useCallback((values: NumberFormatValues, _: SourceInfo) => {
        setTotalCost(values.floatValue);
    }, []);

    const updateUserData = (profileUrl: string, imageId: number) => {
        if (!!updateUser) {
            updateUser(profileUrl, imageId, firstnameRef.current?.value, lastnameRef.current?.value, emailRef.current?.value, locationRef.current?.value);
        }
    }

    return (<>
        <div>
            <Card data-testid="yourDetails" className={scss.yourDetailsCard}>
                <Card.Body>
                    <h2 style={{ fontWeight: "bold" }}>Your Details</h2>
                    { !isLoggedIn && <div className={scss.infoText}>Have an Easy Weddings account? <button className={scss.modalOpenButton} data-testid="review-user-login-link" onClick={showLoginModal}>Login here</button></div> }
                    <Form className="review-submission" noValidate validated={validated} onSubmit={submitReview}>
                        <div className="row gap-2 w-100 m-0 pb-2">
                            <div className="col p-0">
                                <InputGroup>
                                    <InputGroup.Text><i className="fal fa-user"></i></InputGroup.Text>
                                        <FormControl
                                            type="text"
                                            data-testid="review-user-firstname"
                                            placeholder="First Name *"
                                            required
                                            ref={firstnameRef}
                                        />
                                    <Form.Control.Feedback type="invalid">
                                        First name is required
                                    </Form.Control.Feedback>  
                                </InputGroup>
                            </div>
                            <div className="col p-0">
                                <FormControl
                                    type="text"
                                    data-testid="review-user-lastname"
                                    placeholder="Last Name *"
                                    required
                                    ref={lastnameRef}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Last name is required
                                </Form.Control.Feedback>  
                            </div>
                        </div>
                        <div className="row gap-2 w-100 m-0 pb-2">
                            <div className="col p-0">
                                <InputGroup>
                                    <InputGroup.Text><i className="fal fa-envelope"></i></InputGroup.Text>
                                        <FormControl
                                            type="email"
                                            data-testid="review-user-email"
                                            placeholder="Email *"
                                            required
                                            ref={emailRef}
                                        />
                                    <Form.Control.Feedback type="invalid">
                                        Email is required
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </div>
                        </div>
                        <div className="row gap-2 w-100 m-0 pb-2">
                            <div className="col-12 col-md p-0">
                                <InputGroup>
                                    <InputGroup.Text><i className="fal fa-map-marker-alt"></i></InputGroup.Text>
                                        <FormControl
                                            type="text"
                                            data-testid="review-user-location"
                                            placeholder="Location *"
                                            required
                                            pattern={isUK ? UK_LOCATION_PATTERN.source : AU_LOCATION_PATTERN.source}
                                        ref={locationRef}
                                            onChange={onLocationChange}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Location is required
                                        </Form.Control.Feedback>
                                        <LocationSearchResultContainer searchTerm={deferredSearchTerm} onSelectLocation={onSelectLocation} locationSearchUrl={locationSearchUrl}/>
                                </InputGroup>
                            </div>
                            <div className="col-12 col-md p-0">
                                <InputGroup>
                                    <InputGroup.Text><i className="fal fa-calendar-alt"></i></InputGroup.Text>
                                    <div className={`round-border p-0 form-control ${validated ? (validWeddingDate(weddingDate) ? 'is-valid' : 'is-invalid') : ''}`}>
                                        <DatePicker 
                                            placeholderText={'Wedding Date *'}
                                            selected={weddingDate}
                                            onChange={(date) => setWeddingDate(date)}
                                            data-testid="review-user-wedding-date" 
                                            showMonthDropdown
                                            showYearDropdown
                                            dateFormat="dd/MM/yyyy"
                                            dropdownMode="select"
                                            required
                                        />
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        Past wedding date required
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </div>
                        </div>
                        <div className="row gap-2 w-100 m-0 pb-2">
                            <div className={`col-12 p-0 ${scss.weddingCostContainer}`}>
                                <InputGroup>
                                    <InputGroup.Text><i className="fal fa-circle-dollar"></i></InputGroup.Text>

                                    <NumericFormat
                                        className={'me-2 form-control'}
                                        thousandSeparator={true}
                                        prefix={currencySymbol}
                                        placeholder={'Wedding Cost'}
                                        getInputRef={weddingCostRef}
                                        onValueChange={handleCostChange}
                                        data-testid="review-user-weddingcost"
                                    />
                                    <OverlayTrigger
                                        placement={'left'}
                                        trigger={['click', 'hover', 'focus']}
                                        overlay={
                                            <Tooltip id={'tooltip-cost-info'}>
                                                We only collect this information for internal research purposes as it helps us provide average wedding cost for future couples.
                                            </Tooltip>
                                        }
                                    >
                                        <Icon icon={faInfoCircle} className={scss.infoIcon} />
                                    </OverlayTrigger>
                                </InputGroup>
                            </div>
                        </div>

                        <div className="mt-3 mb-3">
                            <div className={`form-control ${scss.profileContainer} ${validated ? (!!loggedInUser?.ProfileImage ? 'is-valid' : `is-invalid ${scss.reviewBorderRed}`) : ''}`}>
                                <ClientOnly<UserAvatarProps> Component={UserAvatarComponent}
                                    isFormInput={true}
                                    showImage={true}
                                    stopOnError={true}
                                    profileImage={loggedInUser?.ProfileImage}
                                    userId={!!loggedInUser?.UserId ? loggedInUser?.UserId : ''}
                                    buttonText={'Add a profile image'}
                                    setProfileImageUrl={updateUserData}
                                    showErrorToast={displayError}
                                />
                            </div>
                            <Form.Control.Feedback type="invalid">
                                Review image is required
                            </Form.Control.Feedback>
                        </div>
                        {categoryInfo?.competitionEnabled && <div>
                            <Checkbox className={scss.checkComp} isChecked={isComp} onClick={handleIsComp}>
                                <span className={scss.infoText}>
                                    I wish to participate in the Easy Weddings competition and claim my entries once approved
                                </span>
                            </Checkbox>
                        </div>}
                        <div className="pt-2">
                            <Button className="w-100" type="submit" variant="primary" disabled={formSubmitting} data-testid="review-submit">
                                {formSubmitting ? <Loading size={"sm"} /> : <span>Submit My Reviews</span>}
                            </Button>
                        </div>
                    </Form>
                    {categoryInfo?.competitionEnabled && <div className={scss.termsAndConditions}>By submitting this form you are agreeing to the <a href="/competition/terms-and-conditions/" target="_blank">Terms and Conditions</a></div>}
                </Card.Body>
            </Card>            
        </div>
    </>)
}

export default YourDetails